import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'react-hot-toast';

interface Profile {
  id: string;
  user_id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  bio?: string;
  date_of_birth?: string;
  city?: string;
  country?: string;
  is_public: boolean;
}

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
        <p className="text-gray-500">This profile does not exist.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Profile Settings</h2>
          <Button onClick={() => signOut()}>Sign Out</Button>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Username</h3>
            <p className="mt-1">{profile.username}</p>
          </div>

          {(profile.first_name || profile.last_name) && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Name</h3>
              <p className="mt-1">
                {[profile.first_name, profile.last_name].filter(Boolean).join(' ')}
              </p>
            </div>
          )}

          {profile.display_name && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Display Name</h3>
              <p className="mt-1">{profile.display_name}</p>
            </div>
          )}

          {profile.bio && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Bio</h3>
              <p className="mt-1">{profile.bio}</p>
            </div>
          )}

          {profile.date_of_birth && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Date of Birth</h3>
              <p className="mt-1">{new Date(profile.date_of_birth).toLocaleDateString()}</p>
            </div>
          )}

          {(profile.city || profile.country) && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Location</h3>
              <p className="mt-1">
                {[profile.city, profile.country].filter(Boolean).join(', ')}
              </p>
            </div>
          )}

          <div>
            <h3 className="text-sm font-medium text-gray-500">Profile Visibility</h3>
            <p className="mt-1">{profile.is_public ? 'Public' : 'Private'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}